import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import PaymentProcess from '@/components/PaymentProcess';
import { ArrowLeft } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/contexts/LanguageContext';

interface PaymentStepProps {
  event: any;
  registrationData: any;
  onNext: () => void;
  onPrevious: () => void;
}

const translateCategory = (cat: string | undefined, language: string): string => {
  if (!cat) return '';
  if (language === 'es') return cat;
  const map: Record<string, string> = {
    'masculino': "Men's", 'femenino': "Women's", 'mixto': 'Mixed',
  };
  return map[cat.toLowerCase()] || cat;
};

const translateSubcategory = (sub: string | undefined, language: string): string => {
  if (!sub) return '';
  if (language === 'es') return sub;
  const map: Record<string, string> = {
    '1era': 'Division 1', '2nda': 'Division 2', '3era': 'Division 3',
    '4ta': 'Division 4', '5ta': 'Division 5', '6ta': 'Division 6',
  };
  return map[sub.toLowerCase()] || sub;
};

export const PaymentStep = ({ event, registrationData, onNext, onPrevious }: PaymentStepProps) => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [isProcessing, setIsProcessing] = useState(false);

  // Fallback to localStorage if registrationData is incomplete (URL param change timing issue)
  const effectiveData = useMemo(() => {
    const data = { ...registrationData };
    if (!data.category || !data.player1Name) {
      try {
        const pending = localStorage.getItem('pendingRegistrationData');
        if (pending) {
          const parsed = JSON.parse(pending);
          return { ...parsed, ...data };
        }
      } catch {}
    }
    return data;
  }, [registrationData]);
  const handlePaymentSuccess = () => {
    setIsProcessing(false);
    onNext();
  };

  const handlePaymentError = (error: string) => {
    setIsProcessing(false);
    console.error('Payment error:', error);
  };

  const isSplitPayment = effectiveData.paymentMode === 'split';
  const discountPercent = effectiveData.discountPercent || 0;
  
  const calculatePaymentAmount = () => {
    const fullFee = event.entry_fee || 0;
    const discountedFee = discountPercent > 0 ? Math.round(fullFee * (1 - discountPercent / 100) * 100) / 100 : fullFee;
    return isSplitPayment ? Math.round(discountedFee / 2 * 100) / 100 : discountedFee;
  };

  const paymentData = {
    eventId: event.id,
    mode: (effectiveData.modality === 'singles' ? 'individual' : 'parejas') as 'individual' | 'parejas',
    partner: (effectiveData.partnerSelectionMethod === 'friend' || effectiveData.partnerSelectionMethod === 'invitation')
      ? effectiveData.selectedFriend?.full_name || effectiveData.selectedFriend?.email
      : effectiveData.player2Name || null,
    paymentMode: (isSplitPayment ? 'my_part' : 'full_amount') as 'my_part' | 'full_amount',
    amount: calculatePaymentAmount(),
    promoCode: effectiveData.promoCode || undefined,
    discountPercent: effectiveData.discountPercent || undefined,
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{t('payment.paymentInfo')}</h2>
        <p className="text-muted-foreground">
          {t('payment.completePayment')}
        </p>
      </div>

      {/* Registration Summary */}
      <Card className="bg-muted border-border">
        <CardHeader>
          <CardTitle className="text-lg">{t('payment.registrationSummary')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">{t('payment.event')}:</span>
            <span className="font-medium">{event.title}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">{t('payment.modality')}:</span>
            <Badge variant="outline">
              {effectiveData.modality === 'singles' ? t('payment.singles') : t('payment.doubles')}
            </Badge>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">{t('payment.category')}:</span>
            <span className="font-medium">{translateCategory(effectiveData.category, language)}</span>
          </div>
          {effectiveData.subcategory && (
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">{t('payment.subcategory')}:</span>
              <span className="font-medium">{translateSubcategory(effectiveData.subcategory, language)}</span>
            </div>
          )}
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">{t('payment.player1')}:</span>
            <span className="font-medium">{effectiveData.player1Name}</span>
          </div>
          
          {effectiveData.modality === 'dobles' && (
            <>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t('payment.partner')}:</span>
                <span className="font-medium">
                  {(effectiveData.partnerSelectionMethod === 'friend' || effectiveData.partnerSelectionMethod === 'invitation')
                    ? effectiveData.selectedFriend?.full_name || effectiveData.selectedFriend?.email
                    : effectiveData.player2Name}
                </span>
              </div>
              
              {(effectiveData.partnerSelectionMethod === 'friend' || effectiveData.partnerSelectionMethod === 'invitation') && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">{t('payment.paymentType')}:</span>
                  <Badge variant={isSplitPayment ? "secondary" : "default"}>
                    {isSplitPayment ? t('payment.splitPayment') : t('payment.fullPayment')}
                  </Badge>
                </div>
              )}
            </>
          )}
          
          <div className="border-t pt-3 mt-3">
            {discountPercent > 0 && (
              <>
                <div className="flex justify-between items-center text-sm text-muted-foreground">
                  <span>{t('payment.totalToPay')} ({t('registration.dataStep.promo.valid')}):</span>
                  <span className="line-through">${event.entry_fee || 0} MXN</span>
                </div>
                <div className="flex justify-between items-center text-sm text-green-600">
                  <span>{t('registration.dataStep.promo.valid')} {discountPercent}%:</span>
                  <span>-${((event.entry_fee || 0) * discountPercent / 100).toFixed(2)} MXN</span>
                </div>
              </>
            )}
            <div className="flex justify-between items-center text-lg font-semibold">
              <span>{isSplitPayment ? t('payment.yourPart') : t('payment.totalToPay')}:</span>
              <span className="text-green-600">${calculatePaymentAmount().toFixed(2)} MXN</span>
            </div>
            {isSplitPayment && (
              <p className="text-xs text-muted-foreground mt-1">
                {t('payment.splitPaymentNote').replace('{name}', effectiveData.selectedFriend?.full_name || t('payment.partner'))}
              </p>
            )}
            {!isSplitPayment && effectiveData.modality === 'dobles' && effectiveData.partnerSelectionMethod === 'friend' && (
              <p className="text-xs text-muted-foreground mt-1">
                {t('payment.doublesNote')}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Split Payment Info Banner */}
      {isSplitPayment && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <p className="text-sm text-amber-800">
              💡 {t('payment.splitInfoBanner').replace('{name}', effectiveData.selectedFriend?.full_name || t('payment.partner'))}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Payment Component */}
      <PaymentProcess
        paymentData={paymentData}
        eventTitle={event.title}
        eventData={event}
        registrationData={effectiveData}
        onPaymentSuccess={handlePaymentSuccess}
        onPaymentError={handlePaymentError}
        onBack={onPrevious}
      />

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-4 border-t">
        <Button
          variant="outline"
          onClick={onPrevious}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('payment.backToData')}
        </Button>
      </div>
    </div>
  );
};
